import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import {
    ArrowDownward,
    ArrowUpward,
    ExpandLess,
    ExpandMore,
    Refresh,
    Undo,
} from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import { estornarTransacao } from "../../../services/extratoPixFornecedor"
import type { GrupoTransferenciaModel, TransacaoPixModel } from "../../../services/extratoPixFornecedor"
import { ExtratoPixFornecedorActionsTypes } from "../context/extratoPixFornecedorActions"
import { useExtratoPixFornecedorContext } from "../context/useExtratoPixFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    filtroRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    saldoBox: {
        background: "linear-gradient(135deg, #1a3c5e 0%, #2c6e49 100%)",
        borderRadius: 15,
        padding: theme.spacing(2, 3),
        color: "white",
        display: "inline-block",
    },
    grupoCard: {
        border: "1px solid #e3e6f0",
        borderRadius: 8,
        marginBottom: theme.spacing(1),
        overflow: "hidden",
    },
    grupoHeader: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1.5, 2),
        cursor: "pointer",
        backgroundColor: "#f8f9fc",
        gap: theme.spacing(2),
        flexWrap: "wrap",
        "&:hover": { backgroundColor: "#eef1f7" },
    },
    grupoHeaderPendente: {
        backgroundColor: "#fffdf0",
        "&:hover": { backgroundColor: "#fff9d6" },
    },
    grupoData: {
        fontWeight: 600,
        minWidth: 160,
        color: "#2c3e50",
    },
    grupoValor: {
        fontWeight: 600,
        color: "#1cc88a",
        minWidth: 120,
    },
    grupoValorPendente: {
        color: "#f6c23e",
    },
    grupoMeta: {
        fontSize: "0.85em",
        color: "#666",
    },
    badgePendente: {
        background: "#f6c23e",
        color: "#fff",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.75em",
        fontWeight: 600,
    },
    entradaRow: {
        backgroundColor: "#f1fff4",
        borderLeft: "3px solid #1cc88a",
    },
    estornoRow: {
        backgroundColor: "#fff5f5",
        borderLeft: "3px solid var(--cor-erro)",
    },
    saidaRow: {
        backgroundColor: "#f5f7ff",
        borderLeft: "3px solid #4e73df",
    },
    valorPositivo: {
        color: "#1cc88a",
        fontWeight: 600,
    },
    valorNegativo: {
        color: "var(--cor-erro)",
        fontWeight: 600,
    },
    thFinanceiro: {
        backgroundColor: "#eef4ff",
    },
    resumoTable: {
        marginTop: theme.spacing(1),
    },
    emptyText: {
        color: "#7f8c8d",
        fontStyle: "italic",
        padding: theme.spacing(2),
    },
}))

function GrupoTransferenciaRow({ grupo }: { grupo: GrupoTransferenciaModel }) {
    const classes = useStyles()
    const [aberto, setAberto] = useState(false)
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    const handleEstornar = async (transacao: TransacaoPixModel) => {
        if (!window.confirm("Tem certeza que deseja estornar este pagamento?")) return
        const autorizacao = localStorage.getItem("chaveSistema") ?? ""
        const res = await estornarTransacao(autorizacao, transacao.id)
        if ("retornoErro" in res) {
            alertStart(res.mensagem || "Erro ao estornar", "error")
        } else {
            alertStart("Estornado com sucesso", "success")
        }
    }

    return (
        <div className={classes.grupoCard}>
            <div
                className={`${classes.grupoHeader} ${grupo.pendente ? classes.grupoHeaderPendente : ""}`}
                onClick={() => setAberto(a => !a)}
            >
                {aberto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                <span className={classes.grupoData}>{grupo.dataExibicao}</span>

                {grupo.pendente ? (
                    <span className={classes.badgePendente}>Pendente de repasse</span>
                ) : (
                    <span className={`${classes.grupoValor} ${grupo.pendente ? classes.grupoValorPendente : ""}`}>
                        R$ {grupo.valorTransferenciaFormatado}
                    </span>
                )}

                <span className={classes.grupoMeta}>
                    Vendas: <strong>R$ {grupo.totalVendasFormatado}</strong> ({grupo.quantidadeVendas})
                </span>
                <span className={classes.grupoMeta}>
                    Estornos: <strong>R$ {grupo.totalEstornosFormatado}</strong> ({grupo.quantidadeEstornos})
                </span>
            </div>

            <Collapse in={aberto}>
                <Box p={2}>
                    {grupo.componentes.length === 0 ? (
                        <Typography className={classes.emptyText}>
                            Sem vendas/estornos vinculados a esta transferência.
                        </Typography>
                    ) : (
                        <TableContainer style={{ maxHeight: 400, overflowX: "auto" }}>
                            <Table
                                size="small"
                                stickyHeader
                                style={{ minWidth: 1180, width: "100%" }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: 80 }}>ID</TableCell>
                                        <TableCell style={{ minWidth: 90 }}>Pedido</TableCell>
                                        <TableCell style={{ minWidth: 140 }}>Operação</TableCell>
                                        <TableCell style={{ minWidth: 160 }}>Pagador</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Dt. Criação</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Dt. Efetivação</TableCell>
                                        <TableCell style={{ minWidth: 120 }}>Situação</TableCell>
                                        <TableCell style={{ minWidth: 100 }} className={classes.thFinanceiro} align="right">Valor</TableCell>
                                        <TableCell style={{ minWidth: 90 }} className={classes.thFinanceiro} align="right">Taxa</TableCell>
                                        <TableCell style={{ minWidth: 100 }} className={classes.thFinanceiro} align="right">Líquido</TableCell>
                                        <TableCell style={{ minWidth: 60 }} align="center">Ação</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {grupo.componentes.map(t => {
                                        const rowClass = t.estorno
                                            ? classes.estornoRow
                                            : t.entrada
                                            ? classes.entradaRow
                                            : classes.saidaRow
                                        const liquidoClass = t.entrada
                                            ? classes.valorPositivo
                                            : classes.valorNegativo
                                        return (
                                            <TableRow key={t.id} className={rowClass}>
                                                <TableCell>{t.id}</TableCell>
                                                <TableCell>{t.pedidoFormatado ?? "-"}</TableCell>
                                                <TableCell>
                                                    {t.entrada ? (
                                                        <ArrowUpward
                                                            style={{ color: "#1cc88a", fontSize: 14, marginRight: 4 }}
                                                        />
                                                    ) : (
                                                        <ArrowDownward
                                                            style={{ color: "var(--cor-erro)", fontSize: 14, marginRight: 4 }}
                                                        />
                                                    )}
                                                    {t.nomeServicoFormatado}
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={t.nomePagador ?? ""}
                                                >
                                                    {t.nomePagador ?? "-"}
                                                </TableCell>
                                                <TableCell>{t.dataCriacao ?? "-"}</TableCell>
                                                <TableCell>{t.dataEfetivacao ?? "-"}</TableCell>
                                                <TableCell>{t.nomeSituacao ?? "-"}</TableCell>
                                                <TableCell align="right">{t.valorBrutoFormatado}</TableCell>
                                                <TableCell align="right">{t.valorTaxaFormatado}</TableCell>
                                                <TableCell align="right">
                                                    <span className={liquidoClass}>
                                                        {t.valorLiquidoFormatado}
                                                    </span>
                                                </TableCell>
                                                <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                                    {t.estornarHabilitado && (
                                                        <IconButton
                                                            size="small"
                                                            title="Estornar"
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                handleEstornar(t)
                                                            }}
                                                        >
                                                            <Undo fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Collapse>
        </div>
    )
}

export function ExtratoPixFornecedorView() {
    const classes = useStyles()
    const { dados, dispatch, erroCarregamento, carregarExtrato } = useExtratoPixFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") =>
        enqueueSnackbar(msg, { variant: tipo })

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handlePesquisar = () => {
        if (!dados.dataInicio || !dados.dataFim) {
            alertStart("Informe as datas de início e fim.", "error")
            return
        }
        if (dados.dataInicio > dados.dataFim) {
            alertStart("A data de início não pode ser maior que a data final.", "error")
            return
        }
        carregarExtrato()
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#2c3e50", marginBottom: 4 }}>
                    Extrato de Vendas Pix
                </Typography>
                <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 24 }}>
                    Visualize o extrato detalhado das suas vendas via Pix.
                </Typography>

                {/* Filtro */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardHeaderTitle}>Período</Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filtroRow}>
                            <TextField
                                label="Data início"
                                type="date"
                                size="small"
                                variant="outlined"
                                value={dados.dataInicio}
                                onChange={e => {
                                    const value = e.target.value
                                    dispatch({
                                        type: ExtratoPixFornecedorActionsTypes.SET_DATA_INICIO,
                                        payload: value,
                                    })
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Data fim"
                                type="date"
                                size="small"
                                variant="outlined"
                                value={dados.dataFim}
                                onChange={e => {
                                    const value = e.target.value
                                    dispatch({
                                        type: ExtratoPixFornecedorActionsTypes.SET_DATA_FIM,
                                        payload: value,
                                    })
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <Button
                                variant="contained"
                                style={{ backgroundColor: "var(--cor-botao-salvar)", color: "white", borderRadius: 25 }}
                                startIcon={<Refresh />}
                                onClick={handlePesquisar}
                            >
                                Atualizar
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Saldo */}
                {dados.saldo && (
                    <Box mb={3}>
                        <Box className={classes.saldoBox}>
                            <Typography variant="h6" style={{ fontWeight: 700 }}>
                                Saldo: R$ {dados.saldo}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Grupos de transferência */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardHeaderTitle}>
                            Transferências e vendas associadas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.grupos.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                A pesquisa não retornou resultados.
                            </Typography>
                        ) : (
                            dados.grupos.map(grupo => (
                                <GrupoTransferenciaRow key={grupo.identificador} grupo={grupo} />
                            ))
                        )}
                    </Box>
                </Paper>

                {/* Resumo por estabelecimento */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography className={classes.cardHeaderTitle}>
                            Resumo por estabelecimento
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.resumo.length === 0 ? (
                            <Typography className={classes.emptyText}>
                                Sem dados para o período selecionado.
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small" className={classes.resumoTable}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Estabelecimento</TableCell>
                                            <TableCell align="right">Total Bruto</TableCell>
                                            <TableCell align="right">Taxas</TableCell>
                                            <TableCell align="right">Estornos</TableCell>
                                            <TableCell align="right">Líquido</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.resumo.map((r, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{r.estabelecimento}</TableCell>
                                                <TableCell align="right">R$ {r.totalBrutoFormatado}</TableCell>
                                                <TableCell align="right">R$ {r.totalTaxasFormatado}</TableCell>
                                                <TableCell align="right">R$ {r.totalEstornosFormatado}</TableCell>
                                                <TableCell align="right">
                                                    <strong>R$ {r.totalLiquidoFormatado}</strong>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
